import { useAddress, useContract } from '@thirdweb-dev/react';
import { NATIVE_TOKEN_ADDRESS } from '@thirdweb-dev/sdk';
import { add } from 'date-fns';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useAsync, useAsyncFn } from 'react-use';

import { config } from '@app/config';
import {
  bigNumberToDecimal,
  numberToBigNumberFixed
} from '@app/helpers/format';
import { getSwapSettings } from '@app/hooks/swap/useSwapSettings';
import { Pair } from '@app/types/pool';

export function useWithdrawLiquidity(pair: Pair | undefined, amount: string) {
  const { query } = useRouter();
  const address = useAddress();
  const { contract } = useContract(config.contracts.router);
  const { contract: pairContract } = useContract(
    pair?.id ?? (query.id as string)
  );

  const { value: pairDecimals } = useAsync(async () => {
    if (!pairContract) {
      return;
    }

    return pairContract.call('decimals');
  }, [pairContract]);

  const [{ loading: approvingWithdraw }, approveWithdraw] =
    useAsyncFn(async () => {
      if (
        !pairContract ||
        !config.contracts.router ||
        !amount ||
        !pairDecimals
      ) {
        return;
      }

      const _amount = numberToBigNumberFixed(+amount, pairDecimals);

      const res = await pairContract.call('approve', [
        config.contracts.router,
        _amount
      ]);

      await getAllowance();

      return res;
    }, [amount, pairContract, pairDecimals]);

  const [{ value: allowance, loading: fetchingAllowance }, getAllowance] =
    useAsyncFn(async () => {
      if (
        !pairContract ||
        !address ||
        !config.contracts.router ||
        !amount ||
        !pairDecimals
      ) {
        return;
      }

      try {
        const res = await pairContract.call('allowance', [
          address,
          config.contracts.router
        ]);

        return bigNumberToDecimal(res, pairDecimals);
      } catch (e) {
        // do nothing
      }
    }, [pairContract, address, amount, pairDecimals]);

  const [{ loading: withdrawingLiquidity, error }, withdrawLiquidity] =
    useAsyncFn(async () => {
      if (!pair || !amount || !contract || !pairDecimals) {
        return;
      }

      const settings = await getSwapSettings();

      const token0 = pair.token0;
      const token1 = pair.token1;

      if (token0.id === NATIVE_TOKEN_ADDRESS) {
        // eth => erc20
        return contract.call(
          'removeLiquidityETHSupportingFeeOnTransferTokens',
          [
            token1.id,
            numberToBigNumberFixed(+amount, +pairDecimals),
            0,
            0,
            address,
            add(new Date(), { minutes: settings.deadline }).getTime()
          ]
        );
      } else if (token1.id === NATIVE_TOKEN_ADDRESS) {
        // erc20 => eth
        return contract.call(
          'removeLiquidityETHSupportingFeeOnTransferTokens',
          [
            token0.id,
            numberToBigNumberFixed(+amount, +pairDecimals),
            0,
            0,
            address,
            add(new Date(), { minutes: settings.deadline }).getTime()
          ]
        );
      } else {
        return contract.call('removeLiquidity', [
          token0.id,
          token1.id,
          numberToBigNumberFixed(+amount, +pairDecimals),
          0,
          0,
          address,
          add(new Date(), { minutes: settings.deadline }).getTime()
        ]);
      }
    }, [contract, amount, pair, allowance, approveWithdraw, pairDecimals]);

  useEffect(() => {
    getAllowance();
  }, [getAllowance]);

  return {
    error,
    allowance,
    getAllowance,
    withdrawingLiquidity,
    fetchingAllowance,
    approvingWithdraw,
    withdrawLiquidity,
    approveWithdraw,
    pairDecimals
  };
}
